import { BuildingType } from '../types/village.js';
const NO_COST = { wood: 0, clay: 0, iron: 0, grain: 0 };
export const BUILDINGS = {
    manor_house: {
        type: BuildingType.MANOR_HOUSE,
        name: 'Manor House',
        description: 'Seat of the lord. Each level speeds every other construction by 5% and unlocks new trades.',
        maxLevel: 30,
        baseCost: { ...NO_COST, wood: 100, clay: 100, iron: 50, grain: 20 },
        costGrowth: 1.25,
        requires: {},
        sprite: 'manor_house',
    },
    timber_camp: {
        type: BuildingType.TIMBER_CAMP,
        name: 'Timber Camp',
        description: 'Woodcutters working the treeline. Produces timber.',
        maxLevel: 30,
        baseCost: { ...NO_COST, wood: 40, clay: 20, iron: 10 },
        costGrowth: 1.2,
        requires: {},
        /*
         * A high floor and a gentle climb.
         *
         * This used to be base 30 growing at 1.16, which put level 1 at 34 an hour
         * against Tribal Wars 2's 120, and made the *pacing* wrong rather than
         * merely the numbers: a first upgrade took 1.7 hours of production where
         * theirs takes 0.4, and a single spearman took an hour and a half to earn.
         * By level 25 the same curve had overshot and we were faster than they
         * were. Steep growth on a low base is exactly backwards - it punishes the
         * hours where a new player decides whether to come back, and pays out in
         * the ones where they are already committed.
         *
         * 85 at 1.123 sits a little under TW2 at every level, which is the right
         * place for a game that is not trying to be it: the same shape of early
         * game, arrived at honestly.
         */
        produces: { resource: 'wood', base: 85, growth: 1.123 },
        sprite: 'timber_camp',
    },
    clay_pit: {
        type: BuildingType.CLAY_PIT,
        name: 'Clay Pit',
        description: 'Riverbank diggings and drying racks. Produces clay.',
        maxLevel: 30,
        baseCost: { ...NO_COST, wood: 20, clay: 40, iron: 10 },
        costGrowth: 1.2,
        requires: {},
        produces: { resource: 'clay', base: 85, growth: 1.123 },
        sprite: 'clay_pit',
    },
    iron_mine: {
        type: BuildingType.IRON_MINE,
        name: 'Iron Mine',
        description: 'Shafts cut into the hillside. The slowest of the three, and the one that arms your men: weapons, armour and warhorses are all paid for in iron.',
        maxLevel: 30,
        baseCost: { ...NO_COST, wood: 30, clay: 30, iron: 40 },
        costGrowth: 1.2,
        requires: {},
        /*
         * Slower than timber or clay, by a constant 72%.
         *
         * Iron is about a quarter of what a village spends on buildings against a
         * third each for timber and clay, so a mine that matched them produced a
         * surplus nobody had a use for. It used to be corrected with a *slower
         * growth rate*, which over-corrected: by level 30 iron had fallen to 64%
         * and kept falling. A flat fraction holds the ratio it is meant to hold at
         * every level instead of drifting.
         */
        produces: { resource: 'iron', base: 61, growth: 1.123 },
        sprite: 'iron_mine',
    },
    farm: {
        type: BuildingType.FARM,
        name: 'Farm',
        description: 'Fields and mills. Produces grain and sets the population ceiling.',
        maxLevel: 30,
        baseCost: { ...NO_COST, wood: 50, clay: 30, grain: 20 },
        costGrowth: 1.2,
        requires: {},
        /*
         * Raised with the rest, or grain becomes the only thing that matters.
         *
         * Grain is the ceiling on an army: it is the one resource that is *spent
         * continuously* rather than once. At the old rate a village with a hundred
         * troops was already running a deficit at farm 10, so tripling the other
         * three without touching this would simply have moved the wall closer.
         *
         * Tuned so a farm filled to its own population cap can just about feed
         * itself and no more, which is the bind this resource exists to create:
         * you can field a full army, but not a full army and a second one.
         */
        produces: { resource: 'grain', base: 110, growth: 1.12 },
        sprite: 'farm',
    },
    warehouse: {
        type: BuildingType.WAREHOUSE,
        name: 'Warehouse',
        description: 'Roofed stores. Raises the cap on every resource you can hold.',
        maxLevel: 30,
        baseCost: { ...NO_COST, wood: 60, clay: 40, iron: 20 },
        costGrowth: 1.25,
        requires: { manor_house: 2 },
        sprite: 'warehouse',
    },
    barracks: {
        type: BuildingType.BARRACKS,
        name: 'Barracks',
        description: 'Drill yard and bunks. Trains infantry; higher levels train faster.',
        maxLevel: 25,
        baseCost: { ...NO_COST, wood: 80, clay: 60, iron: 40 },
        costGrowth: 1.22,
        requires: { manor_house: 2 },
        sprite: 'barracks',
    },
    smithy: {
        type: BuildingType.SMITHY,
        name: 'Smithy',
        description: 'Forge and workshop. Improves every unit you field, boosts iron output, and builds siege engines.',
        maxLevel: 20,
        baseCost: { ...NO_COST, wood: 100, clay: 80, iron: 150, grain: 50 },
        costGrowth: 1.25,
        requires: { manor_house: 5, barracks: 1 },
        sprite: 'smithy',
    },
    stable: {
        type: BuildingType.STABLE,
        name: 'Stable',
        description: 'Paddocks and tack rooms. Trains scouts and cavalry.',
        maxLevel: 20,
        baseCost: { ...NO_COST, wood: 120, clay: 100, iron: 120, grain: 60 },
        costGrowth: 1.25,
        requires: { manor_house: 10, barracks: 5, smithy: 5 },
        sprite: 'stable',
    },
    market: {
        type: BuildingType.MARKET,
        name: 'Market',
        description: 'Stalls and a weighbridge. Sends merchants, and each level puts another on the road.',
        maxLevel: 25,
        baseCost: { ...NO_COST, wood: 90, clay: 70, iron: 50 },
        costGrowth: 1.22,
        requires: { manor_house: 3, warehouse: 2 },
        sprite: 'market',
    },
    wall: {
        type: BuildingType.WALL,
        name: 'Stone Wall',
        description: 'Curtain wall and gatehouse. +5% defence per level, and rams must break through it.',
        maxLevel: 20,
        baseCost: { ...NO_COST, wood: 50, clay: 150, iron: 80 },
        costGrowth: 1.25,
        requires: { manor_house: 5, barracks: 3 },
        sprite: 'wall',
    },
    church: {
        type: BuildingType.CHURCH,
        name: 'Church',
        description: 'Chapel and infirmary. When the village is attacked, a share of the fallen defenders are carried in wounded and walk again.',
        maxLevel: 20,
        baseCost: { ...NO_COST, wood: 110, clay: 150, iron: 60, grain: 40 },
        costGrowth: 1.22,
        requires: { manor_house: 8 },
        sprite: 'church',
    },
    dockyard: {
        type: BuildingType.DOCKYARD,
        name: 'Dockyard',
        description: 'Slipway, quay and crane. Builds ships, and can only be raised in a holding that reaches the sea.',
        /*
         * Fifteen, not twenty. The ships gate at levels 1, 5 and 12, so the top
         * five levels bought nothing but a staffing bill: every building level
         * costs a villager, and a coastal holding with everything maxed was left
         * with 18 free population where the rest of the world gets 23.
         */
        maxLevel: 15,
        baseCost: { ...NO_COST, wood: 160, clay: 90, iron: 50 },
        costGrowth: 1.23,
        requires: { manor_house: 5, warehouse: 3 },
        sprite: 'dockyard',
    },
};
/** Ordered for display on the village screen. */
export const BUILDING_DISPLAY_ORDER = [
    BuildingType.MANOR_HOUSE,
    BuildingType.TIMBER_CAMP,
    BuildingType.CLAY_PIT,
    BuildingType.IRON_MINE,
    BuildingType.FARM,
    BuildingType.WAREHOUSE,
    BuildingType.BARRACKS,
    BuildingType.SMITHY,
    BuildingType.STABLE,
    BuildingType.MARKET,
    BuildingType.CHURCH,
    // Coastal holdings only; the engine refuses it inland, and the list route
    // hides it there so a landlocked village is not taunted with a shipyard.
    BuildingType.DOCKYARD,
    BuildingType.WALL,
];
/**
 * Manor House levels that unlock features beyond the building requirement
 * matrix. Cathedral (15) and Castle Keep (20) are named in the GDD but have
 * no cost or effect defined - they are listed here as not-yet-implemented so
 * the UI can show them greyed out rather than silently omitting them.
 */
export const MANOR_UNLOCKS = [
    { level: 2, label: 'Barracks, Warehouse', implemented: true },
    { level: 3, label: 'Market', implemented: true },
    { level: 5, label: 'Smithy, Stone Wall', implemented: true },
    { level: 10, label: 'Stable', implemented: true },
    { level: 15, label: 'Bailiff, Cathedral', implemented: false },
    { level: 20, label: 'Knights, Castle Keep', implemented: false },
];
export function getBuilding(type) {
    const def = BUILDINGS[type];
    if (!def)
        throw new Error(`Unknown building type: ${type}`);
    return def;
}
//# sourceMappingURL=buildings.js.map